import { ApolloClient } from "@apollo/client";
import { User } from "@archetypes/auth";
import {
  DateFormat,
  ElementType,
  UFormData,
  Question,
  Position,
  UniqueIdLength,
  Page,
  CAMERA_FACING_MODE,
  AspectRatio,
  RESPONSE_VIEW_MODE,
  FormEventPayload,
  FieldRule,
  PRECISION,
  UserFilterType,
  FormMetaData,
  DataModelValue,
  FormType,
  FieldActionType,
} from "@archetypes/form";
import { RatingType } from "@archetypes/form";
import { CountryAndDialCode, CurrencyCode, Entity } from "@archetypes/gql";
import { TimeFormat } from "@archetypes/generic";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { getElementPlaceHolder } from "@utils/element.util";
import { isElementTitleEntityOrUser, isOptionTypeQuestion } from "@utils/question.util";
import { randomIdGenerator } from "@utils/random-id-generator.util";
import { FormResponse, SystemAction } from "@archetypes/responses";
import { evaluateRulesAndGetActionsToBeProcessed, getEventRuleMap } from "@utils/optimus.util";
import { convertNullToUndefinedAndRemoveTypeName } from "@utils/convert-null-to-undefined-and-remove-typename.util";
import { AGGREGATION_OPERATOR } from "@archetypes/operator";

type FormState = Pick<
  UFormData,
  | "formData"
  | "formName"
  | "formDescription"
  | "showQuestionNo"
  | "coverImage"
  | "resetFormAfterSubmit"
  | "countriesAndDialCodes"
  | "countriesCurrencyCodes"
  | "submitButtonText"
  | "users"
  | "entities"
  | "company"
  | "type"
  | "removeBranding"
> & {
  appId?: string;
  pageIndexStack: number[];
  initialMetaData: FormMetaData;
  lastClonedQuestionOrPageId?: string;
  clonedQuestionsMap?: Record<string, string>;
};

const initialState: Record<string, FormState> = {};

export const defaultFormData: FormState = {
  formData: {
    pages: [],
    questionsMap: {},
    ruleTriggers: {},
    fieldRules: [],
    fieldRulesMap: {},
    actionsToBeProcessed: [],
    childFormRecordsMap: {},
  },
  initialMetaData: {
    pages: [],
    questionsMap: {},
    ruleTriggers: {},
    fieldRules: [],
    fieldRulesMap: {},
    actionsToBeProcessed: [],
    childFormRecordsMap: {},
  },
  formName: "",
  formDescription: "",
  showQuestionNo: false,
  coverImage: "",
  countriesAndDialCodes: [],
  countriesCurrencyCodes: [],
  users: [],
  entities: [],
  submitButtonText: "",
  resetFormAfterSubmit: true,
  pageIndexStack: [],
  type: FormType.NORMAL,
  removeBranding: false,
};

interface MoveAndClonePayload {
  currentQuestionId?: string;
  relativeQuestionId?: string;
  targetQuestionPageId?: string;
  position?: Position;
  client: ApolloClient<object>;
  appId: string;
  formId: string;
  pageId: string;
}

function cleanupRulesForQuestions(formState: FormState, questionIds: string[]) {
  const questionIdSet = new Set(questionIds);
  const ruleIdsToDelete = new Set<string>();

  Object.keys(formState.formData.ruleTriggers).forEach((key) => {
    const [triggerQuestionId] = key.split("_");
    if (triggerQuestionId && questionIdSet.has(triggerQuestionId)) {
      (formState.formData.ruleTriggers[key] ?? []).forEach((ruleId) => ruleIdsToDelete.add(ruleId));
    }
  });

  formState.formData.fieldRules = formState.formData.fieldRules.filter(
    (rule) => !questionIdSet.has(rule.questionId) && !ruleIdsToDelete.has(rule.id),
  );

  formState.formData.fieldRulesMap = {};
  formState.formData.fieldRules.forEach((rule) => {
    formState.formData.fieldRulesMap[rule.id] = rule;
  });

  formState.formData.ruleTriggers = getEventRuleMap(formState.formData.fieldRules);
}

const FormSlice = createSlice({
  name: "form",
  initialState,
  reducers: {
    addPage(
      state,
      actionData: PayloadAction<{
        appId: string;
        formId: string;
        pageId: string;
        beforeId: string;
        id: string;
        moveToEnd: boolean;
        client: ApolloClient<object>;
      }>,
    ) {
      const { formId, pageId, beforeId, moveToEnd, id } = actionData.payload;

      const targetPageIndex = state?.[formId].formData.pages.findIndex(({ id }) => id === pageId);

      if (targetPageIndex === -1) {
        return;
      }

      const targetQuestionIndex = state?.[formId].formData.pages[
        targetPageIndex
      ].questions?.findIndex(({ id }) => id === beforeId);

      if (targetQuestionIndex === -1 && !moveToEnd) {
        return;
      }

      // extract questions after the target question
      const questionsAfter =
        targetQuestionIndex !== undefined && targetQuestionIndex !== -1
          ? state?.[formId].formData.pages[targetPageIndex].questions?.slice(targetQuestionIndex)
          : [];

      const newQuestions = questionsAfter?.map((question) => {
        return {
          ...question,
          id: randomIdGenerator(),
        };
      });

      const newPageIndex = moveToEnd ? state?.[formId].formData.pages.length : targetPageIndex + 1;

      const newPage: Page = {
        id,
        title: `Page ${newPageIndex + 1}`,
        description: "",
        allowSubmit: false,
        nextButtonText: "Next",
        questions: moveToEnd ? [] : newQuestions,
      };

      state?.[formId].formData.pages.splice(newPageIndex, 0, newPage);
      // Remove the questions from the original page
      if (!moveToEnd && targetQuestionIndex !== -1) {
        state?.[formId].formData.pages[targetPageIndex].questions?.splice(targetQuestionIndex);
      }
    },
    clonePage(
      state,
      actionData: PayloadAction<{
        appId: string;
        formId: string;
        pageId: string;
        client: ApolloClient<object>;
      }>,
    ) {
      const { formId, pageId } = actionData.payload;

      const clonePageIndex = state?.[formId].formData.pages.findIndex(({ id }) => pageId === id);
      if (clonePageIndex === -1) return;
      const originalPage = state?.[formId].formData.pages[clonePageIndex];
      const {
        title,
        description,
        nextButtonText = "Next",
        allowSubmit = false,
        questions = [],
      } = originalPage;
      const clonedQuestionsMap: Record<string, string> = {};
      //update the question ids only
      const updatedQuestions = questions.map((question) => {
        const newQuestionId = randomIdGenerator();
        clonedQuestionsMap[question.id] = newQuestionId;
        return {
          ...question,
          id: newQuestionId,
        };
      });
      //cloned page with new pageId and questionIds
      const newPageId = randomIdGenerator();
      const clonedPage = {
        id: newPageId,
        title: `${title} (Copy)`,
        description,
        nextButtonText,
        allowSubmit,
        questions: updatedQuestions,
      };
      //pushed the new page to update the state
      state?.[formId].formData.pages.splice(clonePageIndex + 1, 0, clonedPage);
      state[formId].lastClonedQuestionOrPageId = newPageId;
      state[formId].clonedQuestionsMap = clonedQuestionsMap;
    },
    movePage(
      state,
      actionData: PayloadAction<{
        appId: string;
        formId: string;
        moveablePageId: string;
        position: string;
        relativePageId: string;
        client: ApolloClient<object>;
      }>,
    ) {
      const { formId, moveablePageId, position, relativePageId } = actionData.payload;

      const relativePageIndex = state?.[formId].formData.pages.findIndex(
        (page) => page.id === relativePageId,
      );
      const moveablePageIndex = state?.[formId].formData.pages.findIndex(
        ({ id }) => id === moveablePageId,
      );
      const moveablePage = state?.[formId].formData.pages[moveablePageIndex];

      // Remove the moveable page from its original position
      state?.[formId].formData.pages.splice(moveablePageIndex, 1);

      // insert the moveable page at the new position
      state?.[formId].formData.pages.splice(
        position === Position.AFTER ? relativePageIndex + 1 : relativePageIndex,
        0,
        moveablePage,
      );
    },
    addQuestion(
      state,
      actionData: PayloadAction<{
        appId: string;
        formId: string;
        type: ElementType;
        title: string;
        pageId: string;
        id: string;
        beforeId?: string;
        client: ApolloClient<object>;
      }>,
    ) {
      const { type, title, beforeId, appId, formId, pageId, id } = actionData.payload;
      const options: string[] | undefined =
        !isElementTitleEntityOrUser(type) && isOptionTypeQuestion(type)
          ? ["Option 1", "Option 2"]
          : undefined;
      const placeholder = getElementPlaceHolder(type);

      const pageIndex = state?.[formId].formData.pages.findIndex(({ id }) => pageId === id);
      const visible = type !== ElementType.UniqueId;
      const question: Question = {
        id,
        type,
        title,
        content: {
          visible,
          label:
            type === ElementType.TermsAndConditons
              ? "Terms & Condtions"
              : "Question " +
                (state?.[formId].formData.pages[pageIndex].questions.length + 1).toString(),
          required: !visible,
          hintEnabled: false,
          options,
          placeholder,
          locationPicker:
            type === ElementType.LocationPicker
              ? {
                  geofenceConfig: {
                    enabled: false,
                    radius: 25,
                  },
                }
              : undefined,
          frequency:
            type === ElementType.Frequency
              ? { allowIntervalType: true, allowAnytime: true, allowOnCompletionOf: true }
              : undefined,
          user: type === ElementType.Users ? { filterType: UserFilterType.ALL_USERS } : undefined,
          aggregator:
            type === ElementType.Aggregator
              ? { operator: AGGREGATION_OPERATOR.COUNT, appId, formId, offlineAggregation: true }
              : undefined,
          formula:
            type === ElementType.Formula
              ? {
                  expression: "",
                  precision: PRECISION.TWO,
                }
              : undefined,
          date:
            type === ElementType.DatePicker
              ? {
                  format: DateFormat.dayMonthYear,
                }
              : undefined,
          dateRange:
            type === ElementType.DateRangePicker
              ? {
                  format: DateFormat.dayMonthYear,
                }
              : undefined,
          video:
            type === ElementType.Video
              ? {
                  videoUrl: "https://www.youtube.com/watch?v=a3ICNMQW7Ok",
                  videoHeight: 360,
                }
              : undefined,
          rating:
            type === ElementType.Rating
              ? {
                  quantity: 5,
                  type: RatingType.Star,
                }
              : undefined,
          range:
            type === ElementType.Slider
              ? {
                  startValue: 0,
                  endValue: 100,
                  isDualSlider: false,
                  stepSize: 1,
                }
              : undefined,
          fileUpload:
            type === ElementType.FileUpload
              ? {
                  fileTypes: ["Image"],
                  maxFiles: 1,
                }
              : undefined,
          imageUpload:
            type === ElementType.ImageUpload
              ? {
                  maxFiles: 1,
                  allowUploadFromGallery: true,
                  cameraFacingMode: CAMERA_FACING_MODE.REAR,
                  allowSwappingCameraFacingMode: false,
                  aspectRatio: AspectRatio.FourToThree,
                }
              : undefined,
          name:
            type === ElementType.Name
              ? {
                  showMiddleName: false,
                  firstNameRequired: false,
                  lastNameRequired: false,
                  middleNameRequired: false,
                }
              : undefined,
          phoneNumber:
            type === ElementType.MobileNumber
              ? {
                  allowChangingDialCode: true,
                  defaultValue: state?.[formId].countriesAndDialCodes[0],
                }
              : undefined,
          currency:
            type === ElementType.Currency
              ? {
                  allowChangingCurrencyCode: true,
                  defaultValue: state?.[formId].countriesCurrencyCodes[0],
                }
              : undefined,
          termsAndConditions:
            type === ElementType.TermsAndConditons
              ? {
                  content: "Your terms and conditions",
                  declaration: "I accept the Terms and Conditions",
                }
              : undefined,
          timePicker:
            type === ElementType.TimePicker
              ? {
                  timeFormat: TimeFormat.Twelve,
                }
              : undefined,
          uniqueId:
            type === ElementType.UniqueId
              ? { maxLength: UniqueIdLength.THREE, counterStartsFrom: 1 }
              : undefined,
          dataModel:
            type === ElementType.DataModel
              ? {
                  viewMode: RESPONSE_VIEW_MODE.TABLE,
                  appId: state?.[formId].appId as string,
                }
              : undefined,
          repeater:
            type === ElementType.Repeater
              ? {
                  appId: state?.[formId].appId as string,
                  minRecords: 1,
                  maxRecords: 1,
                  allowCreatingNewRecord: true,
                  allowSelectingOldRecord: false,
                }
              : undefined,
        },
      };
      state[formId].formData.questionsMap[question.id] = question;

      if (!beforeId) {
        state[formId].formData.pages[pageIndex].questions.push(question);
      } else {
        const insertAtIndex = state?.[formId].formData.pages[pageIndex].questions.findIndex(
          (el) => el.id === beforeId,
        );
        state?.[formId].formData.pages[pageIndex].questions.splice(insertAtIndex, 0, question);
      }
    },
    updatePage(
      state,
      actionData: PayloadAction<{
        appId: string;
        formId: string;
        page: Page;
        client: ApolloClient<object>;
      }>,
    ) {
      const { formId, page } = actionData.payload;
      const pageIndex = state?.[formId].formData.pages.findIndex(({ id }) => id === page.id);
      state[formId].formData.pages[pageIndex] = { ...page };
    },
    updateQuestion(
      state,
      actionData: PayloadAction<{
        question: Question;
        questionId: string;
        client: ApolloClient<object>;
        appId: string;
        formId: string;
        pageId: string;
      }>,
    ) {
      const { question, questionId, formId, pageId } = actionData.payload;
      const pageIndex = state?.[formId].formData.pages.findIndex(({ id }) => pageId === id);
      const index = state?.[formId].formData.pages[pageIndex].questions.findIndex(
        (question) => question.id === questionId,
      );
      if (index === -1) {
        return;
      }
      state[formId].formData.pages[pageIndex].questions[index] = question;
      state[formId].formData.questionsMap[question.id] = question;
    },
    updateQuestions(
      state,
      actionData: PayloadAction<{ questionPayloadMap: Record<string, Question>; formId: string }>,
    ) {
      const { questionPayloadMap, formId } = actionData.payload;
      state[formId].formData.pages = state[formId].formData.pages.map((page) => {
        return {
          ...page,
          questions: page.questions.map((question) => {
            const newQuestion = questionPayloadMap[question.id];
            if (newQuestion) {
              state[formId].formData.questionsMap[question.id] = newQuestion;
              return newQuestion;
            }
            return question;
          }),
        };
      });
    },
    cloneQuestion(state, actionData: PayloadAction<MoveAndClonePayload>) {
      const { currentQuestionId, formId, pageId } = actionData.payload;
      const pageIndex = state?.[formId].formData.pages.findIndex(({ id }) => pageId === id);
      const initialIndex = state?.[formId].formData.pages[pageIndex].questions.findIndex(
        (question) => question.id === currentQuestionId,
      );

      // Check if indexes are within the bounds of the array
      if (
        initialIndex < 0 ||
        initialIndex >= state?.[formId].formData.pages[pageIndex].questions.length
      ) {
        throw new Error("Question index out of bounds");
      }

      const questionToClone = state?.[formId].formData.pages[pageIndex].questions[initialIndex];
      const newQuestion = {
        ...questionToClone,
        id: randomIdGenerator(),
        content: {
          ...questionToClone.content,
          label: questionToClone.content.label + "(Copy)",
        },
      };

      if (newQuestion.content?.repeater) {
        newQuestion.content.repeater.relationshipFieldId = randomIdGenerator(); // Create a new Question Id for another form
      }
      state[formId].formData.pages[pageIndex].questions.splice(initialIndex + 1, 0, newQuestion);
      state[formId].formData.questionsMap[newQuestion.id] = newQuestion;
      state[formId].lastClonedQuestionOrPageId = newQuestion.id;
    },
    moveQuestion(state, actionData: PayloadAction<MoveAndClonePayload>) {
      const {
        relativeQuestionId,
        position,
        currentQuestionId,
        formId,
        pageId,
        targetQuestionPageId,
      } = actionData.payload;

      if (position === Position.BEGINNING || position === Position.END) {
        //target page index
        const relativePageIndex = state?.[formId].formData.pages.findIndex(
          (page) => page.id === targetQuestionPageId,
        );

        //page and index of the current question
        const { pageIndex: currentPageIndex, questionIndex } =
          state?.[formId].formData.pages
            .map((page, index) => ({
              pageIndex: index,
              questionIndex: page.questions.findIndex(
                (question) => question.id === currentQuestionId,
              ),
            }))
            .find(({ questionIndex }) => questionIndex !== -1) || {};
        if (currentPageIndex !== undefined && questionIndex !== undefined) {
          // Remove the question from its current page
          const [question] = state[formId].formData.pages[currentPageIndex].questions.splice(
            questionIndex,
            1,
          );
          // Add question to the target page at the specified position
          if (position === Position.BEGINNING) {
            state[formId].formData.pages[relativePageIndex].questions.unshift(question);
          } else if (position === Position.END) {
            state[formId].formData.pages[relativePageIndex].questions.push(question);
          }
        }
      } else {
        const pageIndex = state?.[formId].formData.pages.findIndex(
          ({ id }) => targetQuestionPageId === id,
        );
        const sourceQuestionIndex = state?.[formId].formData.pages.findIndex(
          ({ id }) => pageId === id,
        );
        const initialIndex = state?.[formId].formData.pages[
          sourceQuestionIndex
        ].questions.findIndex((question) => question.id === currentQuestionId);
        const tIndex = state?.[formId].formData.pages[pageIndex].questions.findIndex(
          (question) => question.id === relativeQuestionId,
        );

        // case targetIndex > initialIndex
        // BEFORE -> (cI, tI - 1)
        // AFTER -> (cI, tI)
        // case targetIndex < initialIndex
        // BEFORE -> (cI, tI)
        // AFTER -> (cI, tI + 1)

        let targetIndex = -1;

        if (tIndex > initialIndex) {
          targetIndex = position === Position.BEFORE ? Math.max(tIndex - 1, 0) : tIndex + 1;
        } else if (initialIndex > tIndex) {
          targetIndex =
            position === Position.BEFORE
              ? tIndex
              : Math.min(tIndex + 1, state?.[formId].formData.pages[pageIndex].questions.length);
        } else {
          // `tIndex === initialIndex` — no movement
          targetIndex = tIndex;
        }

        // Check if indexes are within the bounds of the array
        // if (
        //   initialIndex < 0 ||
        //   initialIndex > state?.[formId].formData.pages[pageIndex].questions.length ||
        //   targetIndex < 0 ||
        //   targetIndex > state?.[formId].formData.pages[pageIndex].questions.length
        // ) {
        //   throw new Error("Indexes out of bounds");
        // }
        const elementToMove = state?.[formId].formData.pages[sourceQuestionIndex].questions.splice(
          initialIndex,
          1,
        )[0]; // Remove element at initial index
        state?.[formId].formData.pages[pageIndex].questions.splice(targetIndex, 0, elementToMove); // Insert element at target index
      }
    },
    copyQuestion(
      state,
      actionData: PayloadAction<{
        question: Question;
        pageId: string;
        client: ApolloClient<object>;
        appId: string;
        formId: string;
      }>,
    ) {
      const { question, formId, pageId } = actionData.payload;
      const newQuestion = {
        ...question,
        id: randomIdGenerator(),
        content: {
          ...question.content,
          label: question.content.label + "(Copy)",
        },
      };
      state[formId].formData.questionsMap[newQuestion.id] = newQuestion;
      const pageIndex = state?.[formId].formData.pages.findIndex(({ id }) => pageId === id);
      state?.[formId].formData.pages[pageIndex].questions.push(newQuestion);
      if (state && state[formId]) {
        state[formId].lastClonedQuestionOrPageId = newQuestion.id;
      }
    },
    removePage(
      state,
      actionData: PayloadAction<{
        appId: string;
        formId: string;
        pageId: string;
        client: ApolloClient<object>;
      }>,
    ) {
      const { formId, pageId } = actionData.payload;
      const formState = state[formId];
      if (!formState) return;
      const removedQuestionIds: string[] = [];
      formState.formData.pages = formState.formData.pages.filter((page) => {
        if (page.id !== pageId) {
          return true;
        }
        page.questions.forEach((question) => {
          removedQuestionIds.push(question.id);
          delete formState.formData.questionsMap[question.id];
        });
        return false;
      });
      if (removedQuestionIds.length) {
        cleanupRulesForQuestions(formState, removedQuestionIds);
      }
    },
    removeQuestion(
      state,
      actionData: PayloadAction<{
        questionId: string;
        client: ApolloClient<object>;
        appId: string;
        formId: string;
        pageId: string;
      }>,
    ) {
      const { questionId, formId, pageId } = actionData.payload;
      const formState = state[formId];
      if (!formState) return;
      const pageIndex = formState.formData.pages.findIndex(({ id }) => pageId === id);
      const question = formState.formData.pages[pageIndex]?.questions.find(
        (q) => q.id === questionId,
      );
      if (!question) {
        return;
      }
      formState.formData.pages[pageIndex].questions = formState.formData.pages[
        pageIndex
      ].questions.filter((q) => q.id !== questionId);

      cleanupRulesForQuestions(formState, [questionId]);

      delete formState.formData.questionsMap[question.id];
    },
    resetFormState(state, actionData: PayloadAction<{ formId: string }>) {
      const { formId } = actionData.payload;
      if (!state[formId]) {
        return;
      }
      state[formId].formData = state[formId].initialMetaData;
      state[formId].pageIndexStack = [0];
    },
    updateChildFormRecordsMap(
      state,
      actionData: PayloadAction<{
        formId: string;
        value: DataModelValue;
        questionId: string;
        responseId: string;
        addMode?: boolean;
      }>,
    ) {
      const { formId, value, questionId, responseId, addMode } = actionData.payload;
      if (!state[formId]) {
        return;
      }

      state[formId].formData.childFormRecordsMap = state[formId].formData.childFormRecordsMap ?? {};
      const exisitngData = state[formId].formData.childFormRecordsMap[questionId] ?? [];
      if (!addMode) {
        const index = exisitngData.findIndex((item) => item.responseId === responseId);
        if (index === -1) {
          return;
        }
        exisitngData[index] = {
          ...value,
          responseId: responseId.startsWith("temp-") ? responseId : "update-" + responseId,
        };
        state[formId].formData.childFormRecordsMap[questionId] = exisitngData;
      } else {
        state[formId].formData.childFormRecordsMap[questionId] = [...exisitngData, value];
      }
    },
    deleteOrDuplicateChildFormRecordsMap(
      state,
      actionData: PayloadAction<{
        formId: string;
        questionId: string;
        responseId: string;
        temporaryResponseId: string;
        action: SystemAction.DELETE | SystemAction.DUPLICATE;
      }>,
    ) {
      const { formId, questionId, responseId, action, temporaryResponseId } = actionData.payload;
      if (!state[formId]) {
        return;
      }
      state[formId].formData.childFormRecordsMap = state[formId].formData.childFormRecordsMap ?? {};
      const exisitngData = state[formId].formData.childFormRecordsMap[questionId] ?? [];

      if (action === SystemAction.DELETE) {
        state[formId].formData.childFormRecordsMap[questionId] = exisitngData.filter(
          (item) => item.responseId !== responseId,
        );
      } else if (action === SystemAction.DUPLICATE) {
        const index = exisitngData.findIndex((item) => item.responseId === responseId);
        if (index === -1) {
          return;
        }
        const duplicatedItem = {
          ...exisitngData[index],
          responseId: temporaryResponseId,
        };
        state[formId].formData.childFormRecordsMap[questionId] = [
          ...exisitngData.slice(0, index),
          ...exisitngData.slice(index, index + 1),
          duplicatedItem,
          ...exisitngData.slice(index + 1),
        ];
      }
    },
    initializeFormData(
      state,
      actionData: PayloadAction<{
        data: UFormData;
        appId: string;
        formId: string;
        pageId?: string;
        responseTemplateId?: string;
      }>,
    ) {
      const { data, appId, pageId, formId } = actionData.payload;
      const { formData } = data;
      state[formId] = {
        formData: {
          pages: [],
          questionsMap: {},
          ruleTriggers: {},
          fieldRules: [],
          fieldRulesMap: {},
          actionsToBeProcessed: [],
          childFormRecordsMap: {},
        },
        initialMetaData: {
          pages: [],
          questionsMap: {},
          ruleTriggers: {},
          fieldRules: [],
          fieldRulesMap: {},
          actionsToBeProcessed: [],
          childFormRecordsMap: {},
        },
        formName: "",
        formDescription: "",
        showQuestionNo: false,
        coverImage: "",
        countriesAndDialCodes: [],
        countriesCurrencyCodes: [],
        users: [],
        entities: [],
        submitButtonText: "",
        resetFormAfterSubmit: true,
        pageIndexStack: [],
        type: FormType.NORMAL,
        removeBranding: false,
      };
      state[formId].formName = data.formName;
      state[formId].formDescription = data.formDescription;
      state[formId].showQuestionNo = data.showQuestionNo;
      state[formId].coverImage = data.coverImage;
      state[formId].submitButtonText = data.submitButtonText;
      state[formId].resetFormAfterSubmit = data.resetFormAfterSubmit;
      state[formId].company = data.company;
      state[formId].type = data.type;
      state[formId].removeBranding = !!data.removeBranding;
      state[formId].appId = appId;
      state[formId].formData.fieldRules = convertNullToUndefinedAndRemoveTypeName(
        data.formData.fieldRules ?? [],
      );
      const questionIdsWithVisibilityRules = new Set(
        data.formData.fieldRules
          .filter(
            (rule) =>
              rule.action.type === FieldActionType.DISPLAY_FIELD ||
              rule.action.type === FieldActionType.HIDE_FIELD,
          )
          .map((rule) => rule.questionId),
      );

      const updatedPages = (formData.pages ?? []).map((page) => ({
        ...page,
        questions: page.questions.map((question) =>
          !questionIdsWithVisibilityRules.has(question.id)
            ? question
            : {
                ...question,
                content: {
                  ...question.content,
                  visible: false, // hiding questions with visibility rules by default so we can unhide them later after processing rules and not the other way round
                },
              },
        ),
      }));

      state[formId].formData.pages = convertNullToUndefinedAndRemoveTypeName(updatedPages);
      state[formId].formData.actionsToBeProcessed = [];

      state[formId].formData.pages
        .map((page) => page.questions)
        .flat()
        .forEach((question) => {
          state[formId].formData.questionsMap[question.id] = question;
        });

      state[formId].formData.fieldRules.forEach((rule) => {
        state[formId].formData.fieldRulesMap[rule.id] = rule;
      });
      state[formId].formData.ruleTriggers = getEventRuleMap(state[formId].formData.fieldRules);

      state[formId].pageIndexStack = [0];
      if (pageId) {
        const pageIndex = state[formId].formData.pages.findIndex((page) => page.id === pageId);
        if (pageIndex >= 0) {
          state[formId].pageIndexStack = [pageIndex];
        }
      }
      state[formId].initialMetaData = state[formId].formData;
    },
    resetFormData(state) {
      state = {};
      return state;
    },
    updateFormMetaData(
      state,
      actionData: PayloadAction<{
        formId: string;
        formName: string;
        formDescription: string;
        showQuestionNo: boolean;
        coverImage: string;
        submitButtonText: string;
        resetFormAfterSubmit: boolean;
        removeBranding: boolean;
      }>,
    ) {
      const {
        formName,
        formId,
        formDescription,
        showQuestionNo,
        coverImage,
        submitButtonText,
        resetFormAfterSubmit,
        removeBranding,
      } = actionData.payload;
      state[formId].formName = formName;
      state[formId].formDescription = formDescription;
      state[formId].showQuestionNo = showQuestionNo;
      state[formId].coverImage = coverImage;
      state[formId].submitButtonText = submitButtonText;
      state[formId].resetFormAfterSubmit = resetFormAfterSubmit;
      state[formId].removeBranding = removeBranding;
    },
    updateFormGlobalOptions(
      state,
      actionData: PayloadAction<{
        formId: string;
        type:
          | ElementType.Users
          | ElementType.Entities
          | ElementType.Currency
          | ElementType.MobileNumber;
        data?: User[] | Entity[] | CountryAndDialCode[] | CurrencyCode[];
      }>,
    ) {
      const { type, data, formId } = actionData.payload;
      switch (type) {
        case ElementType.Users:
          state[formId].users = (data ?? []) as User[];
          break;
        case ElementType.Entities:
          state[formId].entities = (data ?? []) as Entity[];
          break;
        case ElementType.Currency:
          state[formId].countriesCurrencyCodes = (data ?? []) as CurrencyCode[];
          break;
        case ElementType.MobileNumber:
          state[formId].countriesAndDialCodes = (data ?? []) as CountryAndDialCode[];
          break;
      }
    },
    gotoNextPage(state, actionData: PayloadAction<{ formId: string; nextPageId?: string }>) {
      const { nextPageId, formId } = actionData.payload;
      const nextPageIndex = state[formId].formData.pages.findIndex(
        (page) => page.id === nextPageId,
      );
      if (state[formId].pageIndexStack.length > 0) {
        const currentPageIndex =
          state[formId].pageIndexStack[state[formId].pageIndexStack.length - 1];
        if (nextPageIndex >= 0) {
          state[formId].pageIndexStack.push(nextPageIndex);
        } else if (currentPageIndex < state[formId].formData.pages.length - 1) {
          state[formId].pageIndexStack.push(currentPageIndex + 1);
        }
      }
    },
    gotoPreviousPage(state, actionData: PayloadAction<{ formId: string }>) {
      const { formId } = actionData.payload;
      if (state[formId].pageIndexStack.length > 1) {
        state[formId].pageIndexStack.pop();
      }
    },
    processEvents(
      state,
      actionData: PayloadAction<{
        events: FormEventPayload[];
        formValues?: FormResponse;
        formId: string;
      }>,
    ) {
      const { events, formValues, formId } = actionData.payload;
      const newActionsToBeProcessed = evaluateRulesAndGetActionsToBeProcessed({
        events,
        ruleTriggers: state[formId].formData.ruleTriggers,
        fieldRulesMap: state[formId].formData.fieldRulesMap,
        formValues,
        questionsMap: state[formId].formData.questionsMap,
      });
      state[formId].formData.actionsToBeProcessed = newActionsToBeProcessed;
    },
    updateFieldRules(
      state,
      actionData: PayloadAction<{
        type: "ADD" | "UPDATE" | "DELETE";
        rule: FieldRule;
        client: ApolloClient<object>;
        appId: string;
        formId: string;
      }>,
    ) {
      const { type, rule, formId } = actionData.payload;
      if (type === "DELETE") {
        state[formId].formData.fieldRules = state[formId].formData.fieldRules.filter(
          (r) => r.id !== rule?.id,
        );
      } else if (type === "ADD") {
        state[formId].formData.fieldRules.push(rule);
      } else if (type === "UPDATE") {
        state[formId].formData.fieldRules = state[formId].formData.fieldRules.map((r) => {
          if (r.id !== rule?.id) {
            return r;
          }
          return rule;
        });
      }
      state[formId].formData.fieldRules.forEach((fieldRule) => {
        state[formId].formData.fieldRulesMap[fieldRule.id] = fieldRule;
      });
      state[formId].formData.ruleTriggers = getEventRuleMap(state[formId].formData.fieldRules);
    },
    cloneFieldRules(
      state,
      actionData: PayloadAction<{
        client: ApolloClient<object>;
        appId: string;
        formId: string;
        questionId?: string;
        type: "QUESTION" | "PAGE";
        pageId?: string;
      }>,
    ) {
      const { type, formId, questionId, pageId } = actionData.payload;
      const formState = state?.[formId];

      if (!formState || !formState.formData?.fieldRules) return;

      const rules = formState.formData.fieldRules;
      let clonedRules: FieldRule[] = [];

      // Clone Question Rules
      if (type === "QUESTION" && questionId) {
        const clonedQuestionId = formState.lastClonedQuestionOrPageId;

        clonedRules = rules
          .filter((r) => r.questionId === questionId)
          .map((rule) => ({
            ...rule,
            id: randomIdGenerator(),
            questionId: clonedQuestionId as string,
            pageId: rule.pageId,
          }));
      }
      // Clone Page Rules
      if (type === "PAGE" && pageId) {
        const newPageId = formState.lastClonedQuestionOrPageId;
        const clonedQuestionMap = formState.clonedQuestionsMap || {};

        clonedRules = rules
          .filter((r) => r.pageId === pageId)
          .map((rule) => ({
            ...rule,
            id: randomIdGenerator(),
            pageId: newPageId as string,
            questionId: clonedQuestionMap[rule.questionId],
          }));
      }
      const updatedFieldRules = convertNullToUndefinedAndRemoveTypeName(clonedRules);

      formState.formData.fieldRules = [...formState.formData.fieldRules, ...updatedFieldRules];
    },
  },
});

export const {
  addQuestion,
  updateQuestion,
  updateQuestions,
  moveQuestion,
  cloneQuestion,
  removeQuestion,
  removePage,
  initializeFormData,
  updateFormMetaData,
  updateFormGlobalOptions,
  addPage,
  updatePage,
  gotoNextPage,
  gotoPreviousPage,
  resetFormData,
  clonePage,
  movePage,
  processEvents,
  updateFieldRules,
  cloneFieldRules,
  copyQuestion,
  resetFormState,
  updateChildFormRecordsMap,
  deleteOrDuplicateChildFormRecordsMap,
} = FormSlice.actions;

export default FormSlice.reducer;
